import { Header } from '@/components/header'
import { Footer } from '@/components/footer'
import { useEffect } from 'react'
import { Helmet } from 'react-helmet-async'

export default function BlogPost1() {
  // Handle client-side navigation
  const handleNavigation = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault()
    window.history.pushState({}, '', href)
    window.dispatchEvent(new Event('popstate'))
  }

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])
  
  return (
    <main>
      <Helmet>
        <title>Adding a Deck to Your Calgary Home | Busy Beaver Construction</title>
        <meta name="description" content="Learn about deck building options for Calgary homes including TimberTech composite, pressure-treated lumber, and cedar. Find material comparisons, design considerations, and cost estimates." />
        <meta name="keywords" content="deck building Calgary, custom decks Calgary, TimberTech decks, deck materials Calgary, deck installation cost, wood decks, composite decks, cedar decks" />
        <link rel="canonical" href="https://busybeaverconstruction.com/blog/1" />
        <meta property="og:title" content="Adding a Deck to Your Calgary Home | Busy Beaver Construction" />
        <meta property="og:description" content="Discover the best deck materials for Calgary's climate, design considerations, and investment information for your custom deck project by Calgary's premier deck builder." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://busybeaverconstruction.com/blog/1" />
        <meta property="og:image" content="https://busybeaverconstruction.com/assets/images/Decks/Wood Decks/Multi-level-Cedar-wood-deck.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="article:published_time" content="2023-04-12T00:00:00+00:00" />
        <link rel="preload" href="/assets/images/Decks/Wood Decks/Multi-level-Cedar-wood-deck.jpg" as="image" />
      </Helmet>
      <Header />
      
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center justify-center">
        <img 
          src="/assets/images/Decks/Wood Decks/Multi-level-Cedar-wood-deck.jpg"
          alt="Custom deck in Calgary backyard"
          className="absolute inset-0 w-full h-full object-cover object-bottom loading-eager"
          fetchPriority="high"
        />
        <div className="relative z-10 mt-64 text-center">
          <div className="inline-block bg-busy-green rounded py-3 mx-4 px-4 lg:px-8">
            <p className="text-white text-base font-medium rounded">Calgary's Leading Deck & Outdoor Living Specialist</p>
          </div>
        </div>
      </section>
      
      {/* Blog Content Section */}
      <section className="py-12 md:py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center mb-8 md:mb-14">
            <h1 className="text-center text-5xl lg:text-6xl font-primary">Adding a Deck to Your Calgary Home</h1>
            <p className="text-sm text-beaver-green font-medium mt-4 font-accent">
              Published: April 12, 2023
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            <div className="md:col-span-2 space-y-6 text-base text-gray-600 leading-relaxed">
              <p>
                A well-designed deck is more than just an addition to your home—it's an extension of your living space that enhances both your property's value and your quality of life. For Calgary homeowners, adding a custom deck provides a perfect outdoor retreat during our beautiful but short summer months.
              </p>
              
              <h2 className="text-2xl font-semibold text-text-dark mt-10 mb-4 font-secondary">Why Add a Deck to Your Calgary Home?</h2>
              
              <p>
                Calgary's unique climate presents both challenges and opportunities for outdoor living spaces. With our dry climate and over 333 days of sunshine annually, a deck becomes a natural gathering place for family and friends during the warmer months. Here are some compelling reasons to consider adding a custom deck:
              </p>
              
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Increased Home Value:</strong> A well-built deck can provide up to 70% return on investment when selling your home.</li>
                <li><strong>Extended Living Space:</strong> A deck effectively adds usable square footage to your home without the cost of a full addition.</li>
                <li><strong>Outdoor Entertainment:</strong> Create the perfect space for hosting barbecues, family gatherings, or quiet evenings under the stars.</li>
                <li><strong>Connection with Nature:</strong> Enjoy Calgary's beautiful landscape and mountain views from the comfort of your own backyard.</li>
              </ul>
              
              <h2 className="text-2xl font-semibold text-text-dark mt-10 mb-4 font-secondary">Material Considerations for Calgary's Climate</h2>
              
              <p>
                When choosing materials for your Calgary deck, it's essential to consider our unique climate conditions, including:
              </p>
              
              <ul className="list-disc pl-6 space-y-2">
                <li>Dramatic temperature fluctuations</li>
                <li>Dry conditions with occasional heavy rainfall and hail</li>
                <li>Intense UV exposure during summer months</li>
                <li>Snow and ice during winter</li>
              </ul>
              
              <p>
                At Busy Beaver Construction, we recommend these proven materials for Calgary decks:
              </p>
              
              <h3 className="text-xl font-semibold text-text-dark mt-8 mb-2 font-secondary">1. TimberTech Composite Decking</h3>
              
              <p>
                As a TimberTech Platinum Builder, we highly recommend their premium composite decking for Calgary homes. These low-maintenance composite options provide exceptional durability against our harsh climate while offering the aesthetic appeal of natural wood without the upkeep.
              </p>
              
              <p>
                TimberTech decking resists fading, staining, scratching, and mold—critical features for withstanding Calgary's intense sun and occasional severe weather. The 25-50 year warranties provide peace of mind for homeowners looking for a long-term solution.
              </p>
              
              <h3 className="text-xl font-semibold text-text-dark mt-8 mb-2 font-secondary">2. Pressure-Treated Lumber</h3>
              
              <p>
                For budget-conscious homeowners, pressure-treated lumber remains a viable option. Treated to resist rot, insects, and decay, this material can withstand Calgary's elements when properly maintained. While it requires more regular maintenance than composite options, with proper staining and sealing every 2-3 years, a pressure-treated deck can provide excellent value.
              </p>
              
              <h3 className="text-xl font-semibold text-text-dark mt-8 mb-2 font-secondary">3. Cedar</h3>
              
              <p>
                Western Red Cedar offers natural beauty and inherent resistance to moisture, decay, and insects. Its natural oils help it withstand our climate, though it will weather to a silvery gray over time unless regularly maintained. Cedar provides excellent insulation properties, staying cooler underfoot during hot summer days—a nice feature for barefoot comfort.
              </p>
              
              <h2 className="text-2xl font-semibold text-text-dark mt-10 mb-4 font-secondary">Design Considerations for Calgary Decks</h2>
              
              <p>
                Beyond materials, thoughtful design is crucial for creating a deck that serves your needs while withstanding Calgary's climate:
              </p>
              
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Sun Exposure:</strong> Consider the orientation of your deck and incorporate pergolas or shade structures to provide relief from Calgary's intense summer sun.</li>
                <li><strong>Wind Protection:</strong> Strategic privacy walls or glass railings can create shelter from Calgary's notorious winds while maintaining views.</li>
                <li><strong>Snow Load:</strong> Ensure your deck design accounts for potential snow accumulation during winter months.</li>
                <li><strong>Multi-Level Design:</strong> Create distinct zones for cooking, dining, and lounging to maximize functionality.</li>
                <li><strong>Integrated Lighting:</strong> Extend usable hours with thoughtful lighting solutions that enhance safety and ambiance.</li>
              </ul>
              
              <h2 className="text-2xl font-semibold text-text-dark mt-10 mb-4 font-secondary">Building Permits and Regulations</h2>
              
              <p>
                Before beginning your deck project in Calgary, you'll need to navigate the city's permitting requirements. Generally, you'll need a building permit for decks that are:
              </p>
              
              <ul className="list-disc pl-6 space-y-2">
                <li>Higher than 2 feet (0.6m) above grade</li>
                <li>Attached to the house</li>
                <li>Have a roof structure</li>
              </ul>
              
              <p>
                At Busy Beaver Construction, we handle the entire permitting process for our clients, ensuring all designs meet or exceed Calgary's building codes and regulations.
              </p>
              
              <h2 className="text-2xl font-semibold text-text-dark mt-10 mb-4 font-secondary">The Investment: What to Expect</h2>
              
              <p>
                The cost of adding a deck to your Calgary home varies based on size, materials, complexity, and features. As a general guideline:
              </p>
              
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Pressure-treated decks</strong> typically start around $30-50 per square foot</li>
                <li><strong>Cedar decks</strong> range from $45-70 per square foot</li>
                <li><strong>Composite decks</strong> generally run $60-100+ per square foot</li>
              </ul>
              
              <p>
                Additional features like built-in seating, custom railings, pergolas, outdoor kitchens, or lighting will increase your investment but also enhance the functionality and enjoyment of your space.
              </p>
              
              <h2 className="text-2xl font-semibold text-text-dark mt-10 mb-4 font-secondary">Financing Your Deck Project</h2>
              
              <p>
                At Busy Beaver Construction, we understand that a quality deck represents a significant investment. That's why we've partnered with Financeit to offer flexible payment plans that can make your dream deck a reality for as little as $99 per month. Our team can help you explore financing options that fit your budget.
              </p>
              
              <h2 className="text-2xl font-semibold text-text-dark mt-10 mb-4 font-secondary">Ready to Transform Your Calgary Backyard?</h2>
              
              <p>
                With 25 years of experience building custom decks throughout Calgary, Busy Beaver Construction has the expertise to create the perfect outdoor living space for your home. Our team of professionals handles everything from initial design and permitting to construction and finishing touches.
              </p>
              
              <p className="text-beaver-red font-medium mt-6">
                Contact us today to schedule a free consultation and estimate for your Calgary deck project.
              </p>
              
              <div className="mt-10 pt-6 border-t border-gray-200">
                <h3 className="text-xl font-bold mb-4 font-secondary">Ready to start your project?</h3>
                <div className="flex flex-wrap gap-4">
                  <a 
                    href="/appointment" 
                    className="inline-block px-6 py-3 bg-beaver-green text-black font-medium rounded hover:brightness-110 transition-all duration-300 font-accent"
                    onClick={(e) => handleNavigation(e, '/appointment')}
                  >
                    CONTACT US
                  </a>
                  <a 
                    href="/estimate" 
                    className="inline-block px-6 py-3 bg-black text-white font-medium rounded hover:bg-gray-800 transition-all duration-300 font-accent"
                    onClick={(e) => handleNavigation(e, '/estimate')}
                  >
                    CALCULATE YOUR PROJECT
                  </a>
                </div>
              </div>
            </div>
            
            {/* Sidebar with additional images */}
            <div className="space-y-6">
              <div className="overflow-hidden h-[320px] md:h-[370px]">
                <img 
                  src="/assets/images/Decks/Composite Decks/Timbertech-Legacy-deck-in-Tigerwood-trim-with-Ashwood-surface.-Custom-cable-railing.jpg"
                  alt="TimberTech Composite Deck with Cable Railing"
                  className="w-full h-full object-cover loading-lazy"
                />
              </div>
              <div className="overflow-hidden h-[320px] md:h-[370px]">
                <img 
                  src="/assets/images/Decks/Wood Decks/Cedar-wood-deck-with-Custom-flower-box-and-Privacy-wall.jpg"
                  alt="Cedar Wood Deck with Privacy Wall"
                  className="w-full h-full object-cover loading-lazy"
                />
              </div>
              <div className="overflow-hidden h-[320px] md:h-[370px]">
                <img 
                  src="/assets/images/Decks/Vinyl Decks/Deksmart-Silver-Maple-Vinyl-decking.jpg"
                  alt="Vinyl Deck in Silver Maple"
                  className="w-full h-full object-cover loading-lazy"
                />
              </div>
              <div className="overflow-hidden h-[320px] md:h-[370px]">
                <img 
                  src="/assets/images/Decks/Composite Decks/Timbertech-Terrain-multi-level-deck-in-Brown-Oak-trim-and-Silver-Maple-surface.-LED-strip-lighting-on-stairs.jpg"
                  alt="Multi-level Composite Deck with LED Lighting"
                  className="w-full h-full object-cover loading-lazy"
                />
              </div>
              <div className="overflow-hidden h-[320px] md:h-[370px]">
                <img 
                  src="/assets/images/Decks/Wood Decks/Large-outdoor-seating-area-with-Custom-Cedar-privacy-wall.jpg"
                  alt="Cedar Deck with Outdoor Seating"
                  className="w-full h-full object-cover loading-lazy"
                />
              </div>
              <div className="overflow-hidden h-[320px] md:h-[370px]">
                <img 
                  src="/assets/images/Decks/Composite Decks/Timbertech-multi-level-deck-with-Ashwood-trim-and-Silver-Maple-surface.jpg"
                  alt="Multi-level Composite Deck"
                  className="w-full h-full object-cover loading-lazy"
                />
              </div>
              <div className="overflow-hidden h-[320px] md:h-[370px]">
                <img 
                  src="/assets/images/Decks/Wood Decks/Pressure-Treated-wood-deck-with-Custom-Cedar-Pergola-and-Privacy-Wall.jpg"
                  alt="Pressure-Treated Deck with Pergola"
                  className="w-full h-full object-cover loading-lazy"
                />
              </div>
              <div className="overflow-hidden h-[320px] md:h-[370px]">
                <img 
                  src="/assets/images/Decks/Vinyl Decks/duradek-vinyl-decking-60-768x576-1.webp"
                  alt="Duradek Vinyl Decking"
                  className="w-full h-full object-cover loading-lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </main>
  )
} 